<template>
  <div class="animated fadeIn reports">
    <b-card no-body>
      <b-row>
        <b-col lg="12">
          <b-card no-body class="bg-white">
            <b-row slot="header" class="p-0">
              <b-col sm="3" class="mt-auto">
                <h5>Forms Graph</h5>
              </b-col>
              <b-col sm="1.5" class="mt-auto">
                <h6>{{ $t('COMMON.DATE_RANGE') }}</h6>
              </b-col>
              <b-col sm="3" class="mt-auto">
                <date-picker
                  range
                  v-model="formsDateRange"
                  lang="en"
                  format="YYYY-MM-DD"
                  append-to-body
                  :clearable="false"
                  width="220px"
                />
              </b-col>
              <b-col sm="1" class="mt-auto">
                <b-button @click="fetchCounts">Fetch</b-button>
              </b-col>
              <b-col sm="2" class="mt-auto">
                <b-button @click="downloadFormsGraphPDF">Download PDF</b-button>
              </b-col>
            </b-row>
            <b-card-body>
              <b-row>
                <b-col md="12" lg="12">
                  <b-card no-body class="chart-card" id="processed-forms-chart">
                    <b-card-body class="chart-card-body">
                      <h6 class="chart-title">Processed Forms Count</h6>
                      <BarChart
                        :bar-labels="formChartLabels"
                        :bar-data="formChartDataPoints"
                        :y-step-size="processedFormsYStepSize"
                        :y-max="processedFormsYMax"
                        :dataset-label="$t('ANALYTICS.FORM_COUNT')"
                      />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </b-card-body>
            <b-card-body>
              <b-row>
                <b-col md="12" lg="12">
                  <b-card no-body class="chart-card" id="unique-users-chart">
                    <b-card-body class="chart-card-body">
                      <h6 class="chart-title">Unique Users Count</h6>
                      <BarChart
                        :bar-labels="userChartLabels"
                        :bar-data="userChartDataPoints"
                        :y-step-size="uniqueUsersYStepSize"
                        :y-max="uniqueUsersYMax"
                        :dataset-label="$t('ANALYTICS.USER_COUNT')"
                      />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col lg="12">
          <b-card no-body class="bg-white">
            <b-row slot="header" class="p-0">
              <b-col sm="3" class="mt-auto">
                <h5>Forms Tables</h5>
              </b-col>
              <b-col sm="1" class="mt-auto">
                <h6>{{ $t('COMMON.DATE_RANGE') }}</h6>
              </b-col>
              <b-col sm="3" class="mt-auto">
                <date-picker
                  range
                  v-model="tablesDateRange"
                  lang="en"
                  format="YYYY-MM-DD"
                  append-to-body
                  :clearable="false"
                  width="220px"
                />
              </b-col>
              <b-col sm="2" class="mt-auto">
                <h6>Filter by Brand</h6>
              </b-col>
              <b-col sm="2" class="mt-auto">
                <b-form-select 
                  v-model="selectedBrand" 
                  :options="allBrandOptions"
                />
              </b-col>
              <b-col sm="1" class="mt-auto">
                <b-button @click="fetchBrandsData">Fetch</b-button>
              </b-col>
            </b-row>
            <b-card-body>
              <b-row>
                <b-col md="12" lg="12">
                  <b-card no-body bg-variant="light" class="table-card">
                    <b-card-header class="table-subheader">
                      <b-row class="p-0 align-items-center">
                        <b-col sm="8">
                          <h6 class="mb-0">Brands Analysis Table</h6>
                        </b-col>
                        <b-col sm="2" class="mt-1">
                          <b-button v-on:click="downloadBrandsTable">Download CSV</b-button>
                        </b-col>
                      </b-row>
                    </b-card-header>
                    <b-card-body class="p-3">
                      <div class="report-table">
                        <b-table 
                          show-empty 
                          striped 
                          :empty-text="$t('COMMON.NO_RECORD')" 
                          hover 
                          responsive="sm" 
                          :items="filteredBrandFields" 
                          :fields="brandTableFields"
                          :sort-by.sync="brandSortBy"
                          :sort-desc.sync="brandSortDesc"
                        >
                          <template slot="HEAD_month" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                          <template slot="HEAD_brand" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                          <template slot="HEAD_avg_transcribed_fields" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                          <template slot="HEAD_avg_edited_fields" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                        </b-table>
                        <content-placeholders v-if="!showBrandsTable">
                          <content-placeholders-text :lines="12"/>
                        </content-placeholders>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
              <b-row class="mt-4">
                <b-col md="12" lg="12">
                  <b-card no-body bg-variant="light" class="table-card">
                    <b-card-header class="table-subheader">
                      <b-row class="p-0 align-items-center">
                        <b-col sm="8">
                          <h6 class="mb-0">Most Edited Fields Table {{ appliedBrandFilter ? `- ${appliedBrandFilter}` : '- All Brands' }}</h6>
                        </b-col>
                        <b-col sm="2" class="mt-1">
                          <b-button v-on:click="downloadEditedFieldsTable">Download CSV</b-button>
                        </b-col>
                      </b-row>
                    </b-card-header>
                    <b-card-body class="p-3">
                      <div class="report-table">
                        <b-table 
                          show-empty 
                          striped 
                          :empty-text="$t('COMMON.NO_RECORD')" 
                          hover 
                          responsive="sm" 
                          :items="topEditedFields" 
                          :fields="editedFieldsTableFields"
                        >
                          <template slot="HEAD_s_no" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                          <template slot="HEAD_field_name" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                          <template slot="HEAD_edit_count" slot-scope="data">
                            <em>{{ data.label }}</em>
                          </template>
                        </b-table>
                        <content-placeholders v-if="!showBrandsTable">
                          <content-placeholders-text :lines="15"/>
                        </content-placeholders>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import DatePicker from 'vue2-datepicker'
import moment from 'moment'
import BarChart from './BarChart.vue'

export default {
  name: 'forms-dashboard',
  components: {
    'date-picker': DatePicker,
    BarChart
  },
  computed: {
    ...mapGetters(['idToken']),
    processedFormsYMax () {
      return this.getChartYMax(this.formChartDataPoints)
    },
    processedFormsYStepSize () {
      return this.processedFormsYMax / 10
    },
    uniqueUsersYMax () {
      return this.getChartYMax(this.userChartDataPoints)
    },
    uniqueUsersYStepSize () {
      return this.uniqueUsersYMax / 10
    },
    allBrandOptions () {
      const uniqueBrands = new Set()
      this.monthlyBrandsData.forEach(monthData => {
        monthData.brands.forEach(brand => uniqueBrands.add(brand.brand))
      })
      const brands = Array.from(uniqueBrands).map(b => ({ value: b, text: b }))
      return [{ value: null, text: 'All Brands' }, ...brands]
    },
    filteredBrandFields () {
      const allRows = []
      this.monthlyBrandsData.forEach(monthData => {
        monthData.brands.forEach(brand => {
          if (!this.appliedBrandFilter || brand.brand === this.appliedBrandFilter) {
            allRows.push({
              month: monthData.month,
              brand: brand.brand,
              avg_transcribed_fields: brand.avg_transcribed_fields,
              avg_edited_fields: brand.avg_edited_fields
            })
          }
        })
      })
      return allRows
    },
    topEditedFields () {
      if (!this.editedFieldsData || this.editedFieldsData.length === 0) {
        return []
      }
      let filteredFields = this.editedFieldsData
      if (this.appliedBrandFilter) {
        filteredFields = this.editedFieldsData.filter(field =>
          field.brand === this.appliedBrandFilter
        )
      }
      const fieldEditCounts = {}
      filteredFields.forEach(field => {
        if (fieldEditCounts[field.field_name]) {
          fieldEditCounts[field.field_name] += field.edit_count
        } else {
          fieldEditCounts[field.field_name] = field.edit_count
        }
      })
      const sortedFields = Object.keys(fieldEditCounts).map(fieldName => ({
        field_name: fieldName,
        edit_count: fieldEditCounts[fieldName]
      })).sort((a, b) => b.edit_count - a.edit_count)
      return sortedFields.slice(0, 15).map((field, index) => ({
        s_no: index + 1,
        field_name: field.field_name,
        edit_count: field.edit_count
      }))
    }
  },
  data () {
    return {
      formsDateRange: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()],
      tablesDateRange: [new Date(), new Date()],
      userChartLabels: [],
      userChartDataPoints: [],
      formChartLabels: [],
      formChartDataPoints: [],
      monthlyBrandsData: [],
      editedFieldsData: [],
      selectedBrand: null,
      appliedBrandFilter: null,
      showBrandsTable: true,
      brandSortBy: 'month',
      brandSortDesc: true,
      brandTableFields: [
        { key: 'month', label: 'Month', sortable: true },
        { key: 'brand', label: 'Brand', sortable: true },
        { key: 'avg_transcribed_fields', label: 'Avg. Transcribed Fields', sortable: true },
        { key: 'avg_edited_fields', label: 'Avg. Edited Fields', sortable: true }
      ],
      editedFieldsTableFields: [
        { key: 's_no', label: 'S.No.', sortable: false },
        { key: 'field_name', label: 'Field Name', sortable: false },
        { key: 'edit_count', label: 'Edit Count', sortable: false }
      ]
    }
  },
  created () {
    this.userChartLabels = this.buildLabelsFromRange(this.formsDateRange)
    this.formChartLabels = this.buildLabelsFromRange(this.formsDateRange)
    this.fetchCounts()
    this.fetchBrandsData()
  },
  methods: {
    getChartYMax (dataPoints) {
      const maxValue = Math.max(...dataPoints, 0)
      return maxValue <= 100 ? 100 : Math.ceil(maxValue / 50) * 50
    },
    getCanvasImageWithBackground (sourceCanvas, backgroundColor = '#ffffff') {
      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = sourceCanvas.width
      exportCanvas.height = sourceCanvas.height
      const exportContext = exportCanvas.getContext('2d')
      if (!exportContext) {
        return sourceCanvas.toDataURL('image/png')
      }
      exportContext.fillStyle = backgroundColor
      exportContext.fillRect(0, 0, exportCanvas.width, exportCanvas.height)
      exportContext.drawImage(sourceCanvas, 0, 0)
      return exportCanvas.toDataURL('image/png')
    },
    buildLabelsFromRange (range) {
      const start = moment(range[0]).startOf('day')
      const end = moment(range[1]).startOf('day')
      const labels = []
      const cur = start.clone()
      while (cur.isSameOrBefore(end)) {
        labels.push(cur.format('YYYY-MM-DD'))
        cur.add(1, 'day')
      }
      return labels
    },
    async fetchCounts () {
      const startDate = moment(this.formsDateRange[0]).format('YYYY-MM-DD')
      const endDate = moment(this.formsDateRange[1]).format('YYYY-MM-DD')
      const params = { start_date: startDate, end_date: endDate }
      try {
        const res = await Vue.axios.post('reports/forms_dashboard_counts', params, {
          headers: { Authorization: `Bearer ${this.idToken}` }
        })
        const userCounts = res.data.user_counts || []
        const formCounts = res.data.form_counts || []
        const labels = this.buildLabelsFromRange(this.formsDateRange)
        const userCountsByDate = {}
        userCounts.forEach(item => {
          const date = moment(item.created_date).format('YYYY-MM-DD')
          userCountsByDate[date] = item.unique_users || 0
        })
        const userDataPoints = labels.map(date => userCountsByDate[date] || 0)
        const formCountsByDate = {}
        formCounts.forEach(item => {
          const date = moment(item.created_date).format('YYYY-MM-DD')
          formCountsByDate[date] = item.document_count || 0
        })
        const formDataPoints = labels.map(date => formCountsByDate[date] || 0)
        this.userChartLabels = labels
        this.userChartDataPoints = userDataPoints
        this.formChartLabels = labels
        this.formChartDataPoints = formDataPoints
      } catch (error) {
        console.error(error)
        if (error && error.status === 401) {
          Vue.toasted && Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
        } else {
          Vue.toasted && Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
        }
      }
    },
    async fetchBrandsData () {
      const startDate = moment(this.tablesDateRange[0]).format('YYYY-MM-DD')
      const endDate = moment(this.tablesDateRange[1]).format('YYYY-MM-DD')
      const params = { start_date: startDate, end_date: endDate }

      try {
        const res = await Vue.axios.post('reports/forms_dashboard_tables', params, {
          headers: { Authorization: `Bearer ${this.idToken}` }
        })
        const data = res.data.data || []
        const editedFields = res.data.edited_fields || []
        this.monthlyBrandsData = data
        this.editedFieldsData = editedFields
        this.appliedBrandFilter = this.selectedBrand
      } catch (error) {
        console.error(error)
        if (error && error.status === 401) {
          Vue.toasted && Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
        } else {
          Vue.toasted && Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
        }
      }
    },
    downloadBrandsTable () {
      let payload = {
        start_date: moment(this.tablesDateRange[0]).format('YYYY-MM-DD'),
        end_date: moment(this.tablesDateRange[1]).format('YYYY-MM-DD')
      }
      let title = 'brands_table_'
      Vue.axios.post('reports/form_brands.csv', payload, {
        headers: { 'Authorization': `Bearer ${this.idToken}` }
      }).then(res => {
        let csvData = res.data
        var downloadLink = document.createElement('a')
        downloadLink.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData)
        downloadLink.download = title + moment().format('YYYY-MM-DD') + '.csv'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      }).catch(error => {
        console.error(error)
        if (error.status === 401) {
          Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
        } else {
          Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
        }
      })
    },
    downloadEditedFieldsTable () {
      let payload = {
        start_date: moment(this.tablesDateRange[0]).format('YYYY-MM-DD'),
        end_date: moment(this.tablesDateRange[1]).format('YYYY-MM-DD'),
        brand: this.appliedBrandFilter || null
      }
      let title = 'edited_fields_table_'
      Vue.axios.post('reports/form_fields.csv', payload, {
        headers: { 'Authorization': `Bearer ${this.idToken}` }
      }).then(res => {
        let csvData = res.data
        var downloadLink = document.createElement('a')
        downloadLink.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData)
        downloadLink.download = title + moment().format('YYYY-MM-DD') + '.csv'
        document.body.appendChild(downloadLink)
        downloadLink.click()
        downloadLink.remove()
      }).catch(error => {
        console.error(error)
        if (error.status === 401) {
          Vue.toasted.show(this.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
        } else {
          Vue.toasted.show(this.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
        }
      })
    },
    async downloadFormsGraphPDF () {
      try {
        const { jsPDF } = await import('jspdf')
        // Get canvas elements directly from Chart.js
        const processedCanvas = document.querySelector('#processed-forms-chart canvas')
        const usersCanvas = document.querySelector('#unique-users-chart canvas')
        if (!processedCanvas || !usersCanvas) {
          Vue.toasted.show('Charts not found', { icon: 'exclamation-triangle', type: 'error' })
          return
        }
        /* eslint-disable new-cap */
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        })
        /* eslint-enable new-cap */
        const pageWidth = pdf.internal.pageSize.getWidth()
        const margin = 10
        // Add title and date
        pdf.setFontSize(18)
        pdf.setFont(undefined, 'bold')
        pdf.text('Forms Graph Count', pageWidth / 2, 15, { align: 'center' })
        const startDate = moment(this.formsDateRange[0]).format('YYYY-MM-DD')
        const endDate = moment(this.formsDateRange[1]).format('YYYY-MM-DD')
        pdf.setFontSize(12)
        pdf.setFont(undefined, 'normal')
        pdf.text(`Date Range: ${startDate} to ${endDate}`, pageWidth / 2, 22, { align: 'center' })
        // Add first chart (Processed Forms)
        pdf.setFontSize(14)
        pdf.setFont(undefined, 'bold')
        pdf.text('Processed Forms Count', margin, 35)
        const processedImage = this.getCanvasImageWithBackground(processedCanvas)
        pdf.addImage(processedImage, 'PNG', margin, 40, pageWidth - (2 * margin), 80)
        // Add second page
        pdf.addPage()
        pdf.setFontSize(12)
        pdf.setFont(undefined, 'normal')
        // Add second chart (Unique Users)
        pdf.setFontSize(14)
        pdf.setFont(undefined, 'bold')
        pdf.text('Users Count', margin, 35)
        const usersImage = this.getCanvasImageWithBackground(usersCanvas)
        pdf.addImage(usersImage, 'PNG', margin, 40, pageWidth - (2 * margin), 80)
        pdf.save(`forms_graph_${moment().format('YYYY-MM-DD')}.pdf`)
        Vue.toasted.show('PDF downloaded successfully!', { icon: 'check-circle', type: 'success' })
      } catch (error) {
        console.error(error)
        Vue.toasted.show('Failed to generate PDF', { icon: 'exclamation-triangle', type: 'error' })
      }
    }
  }
}
</script>

<style scoped>
.reports {
  .card {
    cursor: pointer;
    background: #ebeef3;
    border-radius: 10px;
    .bar {
      width: 100%;
      height: 10px;
      border-radius: 10px 10px 0 0;
    }
    .card-header {
      border-radius: 10px 10px 0 0;
      background-color: #e7e7e7;
      padding: 1rem;
    }
  }
}
.chart-card {
  background: linear-gradient(180deg, #f9fbff 0%, #eef3ff 100%);
  border: none;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(14, 37, 71, 0.08);
  margin-top: 8px;
}
.chart-card-body {
  padding: 16px 18px;
}
.chart-card-body canvas {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
}
.chart-title {
  margin-bottom: 12px;
  font-weight: 600;
  color: #2c3e50;
}
</style>



// WEBPACK FOOTER //
// src/views/reports/formsDashboard.vue